import { trigger, transition, style, animate } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import dayjs, { Dayjs } from 'dayjs';
import { debounceTime, distinctUntilChanged, forkJoin, map, Observable, Subscription, take } from 'rxjs';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { ApiRegularWithdrawal, RegularWithdrawalFrequencies, ApiWithdrawalPortfolio, ApiRegularWithdrawalLimit, ClientBankAccountOut, NewRegularWithdrawalInput, RegularWithdrawalDraft, RegularWithdrawalModes, RegularWithdrawalDraftsOut } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { availableWithdrawalPeriods } from 'src/app/shared/constants';
import { DropdownBankInput, DropdownInvestmentInput } from 'src/app/shared/money-dropdown-select/money-dropdown-select';
import { ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { RadiosKeyValue } from 'src/app/shared/radio-input-group/radio-input-group';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';

interface WithdrawalInitDataInput {
  withdrawals: ApiRegularWithdrawal[],
  availablePortfolios: ApiWithdrawalPortfolio[],
  allPortfolios: ExpectedOwnedStrategy[],
  withdrawalDrafts: RegularWithdrawalDraftsOut,
}

@Component({
  templateUrl: './money-rent.component.html',
  styleUrl: './money-rent.component.scss',
  standalone: false,
  animations: [
    trigger('slideFromBottom', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateY(100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class MoneyRentComponent implements OnInit, OnDestroy {
  public preloader = {
    regularWithdrawals: true,
    portfolioChange: true,
    newWithdraw: false,
  };
  public apiError = {
    regularWithdrawals: false,
    portfolioChange: false,
  };

  // allow only future dates
  public tomorrow = dayjs().add(1, 'day').startOf('day').toDate();

  public displayNewWithdrawalOverlay = false;
  public unlimitedValidity = 'unlimited' as const;

  public withdrawalDrafts: RegularWithdrawalDraft[] = [];
  public regularWithdrawals: ApiRegularWithdrawal[] = [];
  public withdrawalAvailablePortfolios: ApiWithdrawalPortfolio[] = [];
  public allClientPortfolios: DropdownInvestmentInput[] = [];

  public selectedInvestment?: ApiWithdrawalPortfolio;

  public availablePeriods = availableWithdrawalPeriods;
  public availableBankAccounts: ClientBankAccountOut[] = [];
  public parsedBankAccounts: DropdownBankInput[] = [];

  public valueWithdrawLimits?: ApiRegularWithdrawalLimit;

  private formSub?: Subscription;
  public aml?: boolean;

  public get withdrawalsLength(): number {
    return this.regularWithdrawals.length + this.withdrawalDrafts.length;
  }

  public withdrawForm = new FormGroup({
    amount: new FormControl<number>(0),
    investmentId: new FormControl<number>(-1),
    clientBankAccountId: new FormControl<number>(-1),
    withdrawRegularity: new FormControl<RadiosKeyValue>(availableWithdrawalPeriods[0]),
    validTo: new FormControl<Dayjs | 'unlimited'>(this.unlimitedValidity),
  });

  public constructor(
    private restApiService: RestApiService,
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private modalService: ModalService,
  ) {}

  public ngOnInit(): void {
    const neccessaryEditRequests: [
      Observable<ApiRegularWithdrawal[]>,
      Observable<ApiWithdrawalPortfolio[]>,
      Observable<ExpectedOwnedStrategy[]>,
      Observable<RegularWithdrawalDraftsOut>,
    ] = [
      this.restApiService.getRegularWithdrawals(),
      this.restApiService.getWithdrawalPortfolios(),
      this.restApiService.getPortfoliosAndStrategies(),
      this.restApiService.getRegularWithdrawalDrafts(),
    ];

    forkJoin(neccessaryEditRequests).pipe(
      take(1),
      map(([withdrawals, availablePortfolios, allPortfolios, withdrawalDrafts]:
        [ApiRegularWithdrawal[], ApiWithdrawalPortfolio[], ExpectedOwnedStrategy[], RegularWithdrawalDraftsOut]) =>
        ({ withdrawals, availablePortfolios, allPortfolios, withdrawalDrafts })
      ),
    ).subscribe({
        error: this.handleWithdrawalsError.bind(this),
        next: this.handleWithdrawalsData.bind(this),
    });
  }

  private onPortfolioChange(): void {
    this.selectedInvestment = this.withdrawalAvailablePortfolios.find(
      investment => investment.portfolioId === this.withdrawForm.value['investmentId']
    );

    if(!this.selectedInvestment) {
      throw new Error('Portfolio change triggered without selected investment!');
    }
    this.preloader.portfolioChange = true;

    const updateRequests: [
      Observable<ClientBankAccountOut[]>,
      Observable<ApiRegularWithdrawalLimit>,
    ] = [
      this.restApiService.getAvailableBankAccounts(this.selectedInvestment.portfolioId),
      this.restApiService.getWithdrawalLimits(
        this.selectedInvestment.portfolioId,
        this.withdrawForm.value.withdrawRegularity?.value as unknown as RegularWithdrawalFrequencies),
    ];

    forkJoin(updateRequests).pipe(
      take(1),
      map(([bankAccounts, limits]: [ClientBankAccountOut[], ApiRegularWithdrawalLimit]) =>
        ({ bankAccounts, limits })
      ),
    ).subscribe({
        error: this.handlePortfolioChangeError.bind(this),
        next: this.handlePortfolioChangeData.bind(this),
    });
  }

  private handlePortfolioChangeData(data: {bankAccounts: ClientBankAccountOut[], limits: ApiRegularWithdrawalLimit}): void {
    this.preloader.portfolioChange = false;
    this.apiError.portfolioChange = false;

    this.availableBankAccounts = data.bankAccounts;
    this.valueWithdrawLimits = data.limits;
    this.parsedBankAccounts = this.availableBankAccounts.map((bankAccount) => {
      return {
        name: bankAccount.accountNumber,
        accountNumber: bankAccount.accountNumber + '/' + bankAccount.bankCode,
        id: bankAccount.clientBankAccountId,
      }
    });
  }

  private handlePortfolioChangeError(error: unknown): void {
    this.preloader.portfolioChange = false;
    this.apiError.portfolioChange = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public hideNewWithdrawalOverlay(): void {
    this.displayNewWithdrawalOverlay = false
  }

  private handleWithdrawalsData(input: WithdrawalInitDataInput) {
    this.withdrawalAvailablePortfolios = input.availablePortfolios;
    this.withdrawalDrafts = input.withdrawalDrafts.regularWithdrawalDrafts;
    this.aml = input.withdrawalDrafts.aml;
    this.regularWithdrawals = input.withdrawals;
    this.allClientPortfolios.push(
      ...input.allPortfolios.map(investment => {
        return {
          name: investment.name,
          type: investment.portfolioType.toLocaleLowerCase() as 'strategy' | 'portfolio',
          actualValue: investment.sumAmount,
          currency: investment.currency,
          id: investment.portfolioId + '',
          containsWithdrawAllRequest: investment.sumOfPendingWithdrawals.containsWithdrawAllRequest,
          sumOfPendingWithdrawals: investment.sumOfPendingWithdrawals.amount,
          valueWithoutPendingWithdrawals: parseInt(investment.sumAmount - investment.sumOfPendingWithdrawals.amount + ''),
          dip: investment.dip,
          minWithdrawalAmount: investment.minWithdrawalAmount,
          efektika: investment.efektika,
        }
      }),
    );
    if(this.withdrawalAvailablePortfolios.length) {
      //init form and change trigger
      if (this.selectedInvestment) {
        this.withdrawForm.patchValue({ investmentId: this.selectedInvestment.portfolioId });
      } else {
        this.withdrawForm.patchValue({ investmentId: this.withdrawalAvailablePortfolios[0].portfolioId });
        this.selectedInvestment = this.withdrawalAvailablePortfolios[0];
      }
      this.initFormSub();
    }
    this.apiError.regularWithdrawals = false;
    this.preloader.regularWithdrawals = false;
  }

  private initFormSub() {
    this.formSub = this.withdrawForm.valueChanges.pipe(
      debounceTime(70),
      distinctUntilChanged((prev, curr) =>
        prev.investmentId === curr.investmentId &&
        prev.withdrawRegularity?.value === curr.withdrawRegularity?.value
      ),
    ).subscribe(() => this.onPortfolioChange());
  }

  public get selectedInvestmentExtended(): DropdownInvestmentInput {
    return this.allClientPortfolios.find(investment => parseInt(investment.id) === this.withdrawForm.value['investmentId'])!;
  }

  public getInvestmentType(investmentId: number): 'strategy' | 'portfolio' {
    const investment = this.allClientPortfolios.find(investment => parseInt(investment.id) === investmentId);
    if(!investment) {
      throw new Error('Withdrawal for unknown investment!');
    }
    return investment.type;
  }

  private handleWithdrawalsError(error: unknown): void {
    this.preloader.regularWithdrawals = false;
    this.apiError.regularWithdrawals = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public updateValidToValue(value: 'unlimited' | string): void {
    this.withdrawForm.patchValue({ validTo: value === this.unlimitedValidity ? value : dayjs(value) });
  }

  public toggleNewWithdrawalOverlay(): void {
    this.displayNewWithdrawalOverlay = true;
  }

  private formValid(): boolean {
    if(!this.valueWithdrawLimits || !this.withdrawForm) {
      return false;
    }
    if(
      (this.amountValueNumber ?? 0) > this.valueWithdrawLimits?.maxAllowedWithdrawal ||
      !this.amountValueNumber ||
      isNaN(this.amountValueNumber)
    ) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.regularWithdrawalOverLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return false;
    }

    if((this.amountValueNumber ?? 0) < this.valueWithdrawLimits?.minAllowedWithdrawal) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.regularWithdrawalUnderLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return false;
    }
    return true;
  }

  private get amountValueNumber(): number {
    return parseInt((this.withdrawForm.value.amount + '').replace(' ', ''));
  }

  public openWithdrawalConfirmModal(): void {
    this.withdrawForm.controls.amount.markAsDirty();
    this.withdrawForm.markAsDirty();
    if(!this.formValid()) {
      return;
    }

    const formData: NewRegularWithdrawalInput = {
      portfolioId: this.withdrawForm.value.investmentId!,
      bankAccountId: this.withdrawForm.value.clientBankAccountId!,
      mode: RegularWithdrawalModes.FIXED_AMOUNT,
      value: this.amountValueNumber,
      frequency: this.withdrawForm.value.withdrawRegularity?.value as unknown as RegularWithdrawalFrequencies,
    };
    if(this.withdrawForm.value.validTo && this.withdrawForm.value.validTo !== this.unlimitedValidity) {
      formData.validTo = this.withdrawForm.value.validTo.format('YYYY-MM-DD');
    }

    this.modalService.openRegularWithdrawConfirmModal({
      formData,
      investmentType: this.selectedInvestmentExtended.type,
      investmentName: this.selectedInvestment?.name ?? '',
      dip: this.selectedInvestmentExtended.dip,
      bankAccountNumber: this.parsedBankAccounts.find(
        account => account.id === this.withdrawForm.value.clientBankAccountId
      )?.accountNumber ?? '',
      new: true,
    });
  }

  public ngOnDestroy(): void {
    this.formSub?.unsubscribe();
  }
}
